// @rpg-unit/ensemble-presets: bands for @rpg/audio's music (tier 3; composition framework, decision 0077). An ensemble
// says which instrument plays each role (the tune, its other colour, the held chords, the broken chords, the bass, a
// fight's drums, a sting), naming instruments by id as data: a preset may depend only on packages and recipes, so it
// can't import the instrument presets; the feature that plays a band (adaptive music) resolves the ids and reports any
// it can't find. Five bands cut from spike 002's music (2026-10-20): the meadow, the town band, the nocturne, the
// taiko layer and the bell sting.
//
// A game takes them as content (`include.json`, or `ENSEMBLE_PRESET_CONTENT` in the browser; the `ensembles` table),
// and turns them with two knobs: `withRole` (another colour: put another instrument in a role) and `without` (sparser:
// leave roles out).
import table from "./content/tables/ensembles.json";
import schema from "./content/schemas/ensembles.schema.json";

/** This unit's version (the manifest's and package.json's). */
export const ENSEMBLE_PRESETS_VERSION = "0.1.0" as const;

/** The roles a band can fill, and what each plays. */
export const ENSEMBLE_ROLES = {
  lead: "the tune (a mood's lead part)",
  alt: "the tune's other colour (a contrasting section's lead)",
  pad: "the held chords",
  arp: "the broken chords (an arpeggio or a strummed pattern)",
  bass: "the bass line",
  drum: "a fight's main drum",
  shaker: "a fight's offbeats",
  fill: "a fight's fill at the bar's end",
  sting: "a short phrase over the music (a win)",
} as const;

/** A role's name. */
export type EnsembleRole = keyof typeof ENSEMBLE_ROLES;

/** One band, as its schema describes it (`src/content/schemas/ensembles.schema.json`). */
export interface EnsemblePreset {
  id: string;
  /** How it feels, in a person's words. */
  feeling: string[];
  /** The instrument (an instrument preset's id) playing each role it has. */
  roles: Partial<Record<EnsembleRole, string>>;
  reason: string;
}

/** The bands as a content table (`{ table: "ensembles", version, rows }`). */
export const ENSEMBLE_PRESETS_TABLE = table as unknown as { table: "ensembles"; version: number; rows: EnsemblePreset[] };

/** The table's row schema (JSON Schema). */
export const ENSEMBLE_PRESETS_SCHEMA = schema as Record<string, unknown>;

/** The content this unit adds to a game, in @rpg/content's shape. */
export const ENSEMBLE_PRESET_CONTENT = {
  package: "@rpg-unit/ensemble-presets",
  version: ENSEMBLE_PRESETS_VERSION,
  tables: [ENSEMBLE_PRESETS_TABLE],
  schemas: { ensembles: ENSEMBLE_PRESETS_SCHEMA },
} as const;

/** The perceptual knobs (rule 13). */
export const ENSEMBLE_PRESET_KNOBS = {
  swap: "another colour: `withRole(band, role, instrument)` puts another instrument in a role (the tune on the bell, the arpeggio on the guitar)",
  sparser: "sparser: `without(band, roles)` leaves roles out (no arpeggio, no bass); the feature drops the parts those roles played",
} as const;

/** The band's instrument ids, each once, in role order. */
export function instrumentIds(e: EnsemblePreset): string[] {
  return [...new Set((Object.keys(ENSEMBLE_ROLES) as EnsembleRole[]).map((r) => e.roles[r]).filter((x): x is string => !!x))];
}

/** A band with `inst` in `role` (a new band; the input is untouched). An unknown role throws. */
export function withRole(e: EnsemblePreset, role: EnsembleRole, inst: string): EnsemblePreset {
  if (!(role in ENSEMBLE_ROLES)) throw new Error(`ensemble presets: no role "${role}"`);
  if (!inst) throw new Error("ensemble presets: an instrument id is needed");
  return { ...structuredClone(e), roles: { ...e.roles, [role]: inst } };
}

/** A band without `roles` (a new band). Leaving out every role, or the tune's bed, is refused by `ensembleIssues`. */
export function without(e: EnsemblePreset, roles: readonly EnsembleRole[]): EnsemblePreset {
  const next = structuredClone(e);
  for (const r of roles) delete next.roles[r];
  return next;
}

/** What's wrong with a set of bands (empty: sound): unique ids, known roles, at least one role, a feeling; a band with
 *  a tune (lead) has a bed under it (a pad or an arpeggio); a band with an alternative colour has a tune to alternate
 *  with; a fight layer (drum) has its main drum. Instrument ids are checked by the feature that resolves them. */
export function ensembleIssues(rows: readonly EnsemblePreset[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const e of rows) {
    if (seen.has(e.id)) out.push(`${e.id}: duplicate id`);
    seen.add(e.id);
    const roles = Object.keys(e.roles);
    if (!roles.length) out.push(`${e.id}: no roles`);
    for (const r of roles) if (!(r in ENSEMBLE_ROLES)) out.push(`${e.id}: unknown role "${r}"`);
    for (const [r, inst] of Object.entries(e.roles)) if (!inst) out.push(`${e.id}: role ${r} names no instrument`);
    if (!e.feeling.length) out.push(`${e.id}: no feeling`);
    if (e.roles.lead && !e.roles.pad && !e.roles.arp) out.push(`${e.id}: a tune with no bed under it (a pad or an arpeggio)`);
    if (e.roles.alt && !e.roles.lead) out.push(`${e.id}: an alternative colour with no tune to alternate with`);
    if ((e.roles.shaker || e.roles.fill) && !e.roles.drum) out.push(`${e.id}: a fight layer with no main drum`);
  }
  return out;
}
